<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import wootConstants from 'dashboard/constants/globals';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const props = defineProps({
  message: {
    type: String,
    default: '',
  },
  isOnPrivateNote: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const { t } = useI18n();

const currentChat = useMapGetter('getSelectedChat');
const currentUser = useMapGetter('getCurrentUser');

const now = ref(Date.now());
let timer = null;

onMounted(() => {
  timer = setInterval(() => {
    now.value = Date.now();
  }, 1000);
});

onUnmounted(() => {
  clearInterval(timer);
});

const assignedAgent = computed({
  get() {
    return currentChat.value?.meta?.assignee;
  },
  set(agent) {
    const agentId = agent ? agent.id : null;
    store.dispatch('setCurrentChatAssignee', {
      conversationId: currentChat.value?.id,
      assignee: agent,
    });
    store.dispatch('assignAgent', {
      conversationId: currentChat.value?.id,
      agentId,
    });
  },
});

const isUserTyping = computed(
  () => props.message !== '' && !props.isOnPrivateNote
);
const isUnassigned = computed(() => !assignedAgent.value);
const isAssignedToOtherAgent = computed(
  () => assignedAgent.value?.id !== currentUser.value?.id
);

const showSelfAssignBanner = computed(() => {
  return (
    isUserTyping.value && (isUnassigned.value || isAssignedToOtherAgent.value)
  );
});

const showBotHandoffBanner = computed(
  () =>
    isUserTyping.value &&
    currentChat.value?.status === wootConstants.STATUS_TYPE.PENDING
);

const botHandoffActionLabel = computed(() => {
  return assignedAgent.value?.id === currentUser.value?.id
    ? t('CONVERSATION.BOT_HANDOFF_REOPEN_ACTION')
    : t('CONVERSATION.BOT_HANDOFF_ACTION');
});

const selfAssignConversation = async () => {
  const { avatar_url, ...rest } = currentUser.value || {};
  assignedAgent.value = { ...rest, thumbnail: avatar_url };
};

const needsAssignmentToCurrentUser = computed(() => {
  return isUnassigned.value || isAssignedToOtherAgent.value;
});

const onClickSelfAssign = async () => {
  try {
    await selfAssignConversation();
    useAlert(t('CONVERSATION.CHANGE_AGENT'));
  } catch (error) {
    useAlert(t('CONVERSATION.CHANGE_AGENT_FAILED'));
  }
};

const reopenConversation = async () => {
  await store.dispatch('toggleStatus', {
    conversationId: currentChat.value?.id,
    status: wootConstants.STATUS_TYPE.OPEN,
  });
};

const onClickBotHandoff = async () => {
  try {
    await reopenConversation();

    if (needsAssignmentToCurrentUser.value) {
      await selfAssignConversation();
    }

    useAlert(t('CONVERSATION.BOT_HANDOFF_SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION.BOT_HANDOFF_ERROR'));
  }
};

const inbox = computed(() => {
  const inboxId = currentChat.value?.inbox_id;
  return store.getters['inboxes/getInbox'](inboxId) || {};
});

const isWuzapiChannel = computed(() => {
  return (
    inbox.value.channel_type === 'Channel::Whatsapp' &&
    inbox.value.provider === 'wuzapi'
  );
});

const isOfficialWhatsApp = computed(() => {
  return (
    inbox.value.channel_type === 'Channel::Whatsapp' &&
    inbox.value.provider !== 'wuzapi'
  );
});

const lastIncomingMessageAt = computed(() =>
  currentChat.value?.last_incoming_message_at || currentChat.value?.contact_last_seen_at
);

const windowEndsAt = computed(() => {
  if (!lastIncomingMessageAt.value) return null;
  return lastIncomingMessageAt.value * 1000 + 24 * 60 * 60 * 1000;
});

const remainingMs = computed(() => {
  if (!windowEndsAt.value) return 0;
  return windowEndsAt.value - now.value;
});

const windowOpen = computed(() => remainingMs.value > 0);

const formatRemaining = ms => {
  if (ms <= 0) return '0h 0m 0s';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours}h ${minutes}m ${seconds}s`;
};

// Uma única barra: mostra apenas para canais WhatsApp oficiais (waba).
// WuConnect não tem restrição de janela (envio livre).
const showWindowBanner = computed(() => {
  return isOfficialWhatsApp.value && currentChat.value?.inbox_id;
});

const POLICY_URL = 'https://whatsappbusiness.com/pt-br/policy/';

const emitTemplateSelect = () => {
  emitter.emit(BUS_EVENTS.OPEN_WHATSAPP_TEMPLATES);
};
</script>

<template>
  <div class="reply-box-banner--root">
    <div
      v-if="showWindowBanner && windowOpen && lastIncomingMessageAt"
      class="cw-window-banner cw-window-open mx-2 mb-2"
    >
      <span class="i-lucide-clock cw-window-clock" />
      <span>
        Janela de conversa aberta. Tempo restante:
        <strong>{{ formatRemaining(remainingMs) }}</strong>
      </span>
    </div>
    <div
      v-if="showWindowBanner && !windowOpen"
      class="cw-window-banner cw-window-closed mx-2 mb-2"
    >
      <span class="i-lucide-clock cw-window-clock" />
      <span class="cw-window-closed-text">
        Janela de 24 horas encerrada. O contato não respondeu nas últimas 24h.
        Envie um template aprovado para retomar a conversa.
        <a
          :href="POLICY_URL"
          target="_blank"
          rel="noopener noreferrer"
          class="cw-window-policy-link"
        >
          Restrições de janela de mensagem de 24 horas
        </a>
      </span>
      <button
        type="button"
        class="cw-window-template-btn flex-shrink-0"
        @click="emitTemplateSelect"
      >
        Selecionar template
      </button>
    </div>
    <Banner
      v-if="showSelfAssignBanner && !showBotHandoffBanner"
      action-button-variant="ghost"
      color-scheme="secondary"
      class="mx-2 mb-2 rounded-lg !py-2"
      :banner-message="$t('CONVERSATION.NOT_ASSIGNED_TO_YOU')"
      has-action-button
      :action-button-label="$t('CONVERSATION.ASSIGN_TO_ME')"
      @primary-action="onClickSelfAssign"
    />
    <Banner
      v-if="showBotHandoffBanner"
      action-button-variant="ghost"
      color-scheme="secondary"
      class="mx-2 mb-2 rounded-lg !py-2"
      :banner-message="$t('CONVERSATION.BOT_HANDOFF_MESSAGE')"
      has-action-button
      :action-button-label="botHandoffActionLabel"
      @primary-action="onClickBotHandoff"
    />
  </div>
</template>

<style scoped>
.cw-window-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: 12px;
  line-height: 1.45;
  border-radius: 8px;
}

.cw-window-clock {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
}

/* Janela ABERTA: verde com leve transparência */
.cw-window-open {
  color: #ffffff;
  background: rgba(16, 185, 129, 0.14);
  border: 1px solid rgba(16, 185, 129, 0.4);
}

.cw-window-open .cw-window-clock {
  color: #ffffff;
}

/* Janela FECHADA: laranja com leve transparência */
.cw-window-closed {
  color: #ffffff;
  background: rgba(249, 115, 22, 0.12);
  border: 1px solid rgba(249, 115, 22, 0.45);
  justify-content: space-between;
  flex-wrap: wrap;
}

.cw-window-closed .cw-window-clock {
  color: #ffffff;
}

.cw-window-closed-text {
  flex: 1 1 auto;
  min-width: 0;
}

.cw-window-policy-link {
  color: #ffffff;
  font-weight: 600;
  font-size: 10px;
  text-decoration: underline;
  margin-left: 2px;
}

.cw-window-policy-link:hover {
  color: #ffffff;
  opacity: 0.85;
}

.cw-window-template-btn {
  flex-shrink: 0;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #ffffff;
  background: rgba(249, 115, 22, 0.18);
  border: 1px solid rgba(249, 115, 22, 0.5);
  border-radius: 6px;
  cursor: pointer;
}

.cw-window-template-btn:hover {
  background: rgba(249, 115, 22, 0.28);
}
</style>
